import React, { Suspense } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useParams, useLocation, matchPath } from 'react-router-dom';

// Import all page components
import { Table } from '../../pages/table/Table';
import { lazyNamed, preloadWhenIdle, ChunkErrorBoundary } from '../../utils/lazyWithRetry';

import { PageHeader } from '../../shared/components/PageHeader';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';

// Clients (Table) is the landing page and stays in the main bundle. Every other page is split
// into its own chunk, loaded when its tab first opens and prefetched in the background once
// the first page is up, so later tabs still open without a wait.
const CreateClient = lazyNamed(() => import('../../pages/createClient/CreateClient'), 'CreateClient');
const ClientInfoPage = lazyNamed(() => import('../../pages/ClientInfoPage/ClientInfoPage'), 'ClientInfoPage');
const BillingReportPage = lazyNamed(() => import('../../pages/bilingReport/BillingReportPage'), 'BillingReportPage');
const SchedulePage = lazyNamed(() => import('../../pages/schedulePage/SchedulePage'), 'SchedulePage');
const PsychosocialAssessmentPage = lazyNamed(() => import('../../pages/PsychosocialAssessmentPage/PsychosocialAssessmentPage'), 'PsychosocialAssessmentPage');
const FormsPage = lazyNamed(() => import('../../pages/formsPage/FormsPage'), 'FormsPage');
const AuthorizationReportPage = lazyNamed(() => import('../../pages/authorizationReportPage/AuthorizationReportPage'), 'AuthorizationReportPage');
const UsersPage = lazyNamed(() => import('../../pages/usersPage/UsersPage'), 'UsersPage');
const UserSchedulePage = lazyNamed(() => import('../../pages/userSchedulePage/UserSchedulePage'), 'UserSchedulePage');
const EmployeeCalendarPage = lazyNamed(() => import('../../pages/employeeCalendarPage/EmployeeCalendarPage'), 'EmployeeCalendarPage');
const ERAPage = lazyNamed(() => import('../../pages/eraPage/ERAPage'), 'ERAPage');
const TestPage = lazyNamed(() => import('../../pages/testPage/TestPage'), 'TestPage');
const DashboardPage = lazyNamed(() => import('../../pages/dashboardPage/DashboardPage'), 'DashboardPage');
const ServicesPage = lazyNamed(() => import('../../pages/servicesPage/ServicesPage'), 'ServicesPage');
const ProvidersPage = lazyNamed(() => import('../../pages/providersPage/ProvidersPage'), 'ProvidersPage');
const RolePermissionsPage = lazyNamed(() => import('../../pages/RolePermissionsPage/RolePermissionsPage'), 'RolePermissionsPage');
const ListManagementPage = lazyNamed(() => import('../../pages/listManagementPage/ListManagementPage'), 'ListManagementPage');
const LAZY_PAGES = [CreateClient, ClientInfoPage, BillingReportPage, SchedulePage, PsychosocialAssessmentPage, FormsPage, AuthorizationReportPage, UsersPage, UserSchedulePage, EmployeeCalendarPage, ERAPage, TestPage, DashboardPage, ServicesPage, ProvidersPage, RolePermissionsPage, ListManagementPage];


// Standardized Bloom-styled header per top-level page. Detail pages (client, assessment)
// keep their own contextual headers and are intentionally omitted.
const PAGE_HEADERS = {
  '/dashboard': { title: 'Dashboard', subtitle: 'Overview and activity', icon: DashboardOutlinedIcon, accent: '#378ADD' },
  // '/home' and '/' (Clients) render their own PageHeader inside TableTab so the metric cards can
  // sit to the right of the title in the same header row (see TableTab).
  '/createClient': { title: 'New Client', subtitle: 'Create a client record', icon: PersonAddAltOutlinedIcon, accent: '#1D9E75' },
  // '/bilingReport' and '/authorizationReport' render their own PageHeader (with metric cards) inside
  // their report components (BilingReport / AuthorizationsReport), so they're intentionally omitted here.
  '/calendar': { title: 'Calendar', subtitle: 'Service schedule', icon: CalendarMonthOutlinedIcon, accent: '#D85A30' },
  '/users': { title: 'Users', subtitle: 'Manage user accounts', icon: ManageAccountsOutlinedIcon, accent: '#185FA5' },
  '/userSchedule': { title: 'User Schedules', subtitle: 'Provider availability', icon: ScheduleOutlinedIcon, accent: '#185FA5' },
  '/employeeCalendar': { title: 'Employee Calendar', subtitle: 'Staff schedule', icon: EventNoteOutlinedIcon, accent: '#D85A30' },
  '/era': { title: 'ERA', subtitle: 'Electronic remittance', icon: DescriptionOutlinedIcon, accent: '#854F0B' },
  '/services': { title: 'Services', subtitle: 'Service catalog and rules', icon: MedicalServicesOutlinedIcon, accent: '#8e8741' },
  '/providers': { title: 'Providers', subtitle: 'Provider directory', icon: BadgeOutlinedIcon, accent: '#534AB7' },
  '/role-permissions': { title: 'Role Permissions', subtitle: 'Roles and access', icon: AdminPanelSettingsOutlinedIcon, accent: '#993556' },
  '/forms': { title: 'Forms', subtitle: 'Assessment forms', icon: AssignmentOutlinedIcon, accent: '#0F6E56' },
  '/list-management': { title: 'List Management', subtitle: 'Dropdown lists', icon: ListAltOutlinedIcon, accent: '#5F5E5A' },
};

const TabContentContainer = styled.div`
  display: ${props => props.visible ? 'block' : 'none'};
  width: 100%;
  height: 100%;
`;

// Route parameter context to override useParams for tabs
const RouteParamsContext = React.createContext({});

// Custom hook to use tab-specific route params
export const useTabParams = () => {
  const contextParams = React.useContext(RouteParamsContext);
  const routerParams = useParams();
  
  // Use context params if available (from tab), otherwise fall back to router params
  return Object.keys(contextParams).length > 0 ? contextParams : routerParams;
};

// Helper to match a path pattern and extract params
const matchRoute = (path, pattern) => {
  return matchPath(pattern, path);
};

// Shown in a tab while its page chunk downloads (usually only on the very first open).
const PageLoading = () => (
  <div style={{ padding: '48px 0', display: 'flex', justifyContent: 'center' }}>
    <div style={{ width: 28, height: 28, borderRadius: '50%', border: '3px solid #D6DDE7', borderTopColor: '#0072E5', animation: 'bloom-spin 0.8s linear infinite' }} />
    <style>{`@keyframes bloom-spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const TabContent = ({ tab, isActive }) => {
  // Extract params from the tab's path
  const pathParams = React.useMemo(() => {
    const routes = [
      { pattern: '/client/:id', component: ClientInfoPage },
      { pattern: '/psychosocialAssessment/:clientId/:formId/:clientServiceId', component: PsychosocialAssessmentPage },
    ];

    for (const route of routes) {
      const match = matchRoute(tab.path, route.pattern);
      if (match) {
        return match.params;
      }
    }
    return {};
  }, [tab.path]);

  // Render the appropriate component based on path
  const renderContent = () => {
    const path = tab.path;

    if (path === '/home' || path === '/') {
      return <Table />;
    }
    if (path === '/dashboard') {
      return <DashboardPage />;
    }
    if (path.startsWith('/client/')) {
      return <ClientInfoPage />;
    }
    if (path === '/createClient') {
      return <CreateClient />;
    }
    if (path === '/bilingReport') {
      return <BillingReportPage />;
    }
    if (path === '/authorizationReport') {
      return <AuthorizationReportPage />;
    }
    if (path === '/calendar') {
      return <SchedulePage />;
    }
    if (path === '/users') {
      return <UsersPage />;
    }
    if (path === '/userSchedule') {
      return <UserSchedulePage />;
    }
    if (path === '/employeeCalendar') {
      return <EmployeeCalendarPage />;
    }
    if (path === '/era') {
      return <ERAPage />;
    }
    if (path === '/services') {
      return <ServicesPage />;
    }
    if (path === '/providers') {
      return <ProvidersPage />;
    }
    if (path === '/role-permissions') {
      return <RolePermissionsPage />;
    }
    if (path === '/forms') {
      return <FormsPage />;
    }
    if (path === '/list-management') {
      return <ListManagementPage />;
    }
    if (path === '/test') {
      return <TestPage />;
    }
    if (path.startsWith('/psychosocialAssessment/')) {
      return <PsychosocialAssessmentPage />;
    }

    return <div>Page not found</div>;
  };

  const pageHeader = PAGE_HEADERS[tab.path];

  return (
    <TabContentContainer visible={isActive}>
      <RouteParamsContext.Provider value={{...(tab.data || {}), ...pathParams}}>
        {pageHeader && <PageHeader {...pageHeader} />}
        <ChunkErrorBoundary>
          <Suspense fallback={<PageLoading />}>
            {renderContent()}
          </Suspense>
        </ChunkErrorBoundary>
      </RouteParamsContext.Provider>
    </TabContentContainer>
  );
};

export const TabbedContent = () => {
  React.useEffect(() => preloadWhenIdle(LAZY_PAGES), []);
  const { tabs, activeTabId } = useSelector(state => state.tabs);

  return (
    <div style={{ width: '100%', height: '100%' }}>
      {tabs.map(tab => (
        <TabContent
          key={tab.id}
          tab={tab}
          isActive={tab.id === activeTabId}
        />
      ))}
    </div>
  );
};
